import { ABILITIES, ABILITY_NAMES, SKILL_KEYS, type Ability, type SkillKey } from '@shared/rules/constants'
import type { CharacterWithStats } from '@shared/types'
import { SPELLS_BY_ID } from '@shared/rules/data/spells'
import { CLASS_BUILDS_BY_ID } from '@shared/rules/data/classes'
import type { SheetData, SheetSpell } from './sheetData'

/**
 * Exports a stored character. Unlike the wizard path this reads the saved row,
 * so any edits made after creation show up on the printed sheet.
 */
export function characterToSheetData(character: CharacterWithStats): SheetData {
  const { derived } = character
  const classBuild = CLASS_BUILDS_BY_ID.get(character.class as never)

  const speedParts = Object.entries(character.otherSpeeds)
  const speed = speedParts.length
    ? [String(character.speed), ...speedParts.map(([mode, value]) => `${mode} ${value}`)].join(' · ')
    : `${character.speed} ft`

  const { cantrips, spells: known, featCantrips, featSpells } = character.spells
  const spells = [
    ...[...cantrips, ...known].map((id) => toSheetSpell(id)),
    // Feat-granted spells are tagged so the sheet shows where they came from.
    ...[...featCantrips, ...featSpells].map((id) => {
      const row = toSheetSpell(id)
      return row ? { ...row, notes: `${row.notes} · feat` } : null
    }),
  ]
    .filter((s): s is SheetSpell => s !== null)
    .sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))

  const backstory = [
    character.personality && `Personality: ${character.personality}`,
    character.backstory,
  ].filter(Boolean).join('\n\n')

  return {
    name: character.name,
    className: classBuild?.name ?? character.class,
    subclass: character.subclass,
    species: character.species,
    background: character.background,
    level: character.level,
    xp: character.xp,

    size: shortSize(character.size),
    speed,
    armorClass: derived.armorClass,
    shieldEquipped: character.otherProficiencies.armor.includes('Shields') && character.acIsManual,

    hpMax: character.hpMax,
    hpCurrent: character.hpCurrent,
    hpTemp: character.hpTemp,
    hitDiceMax: derived.hitDie ? `${character.hitDiceTotal}d${derived.hitDie}` : '',
    hitDiceSpent: character.hitDiceSpent,

    proficiencyBonus: derived.proficiencyBonus,
    initiative: derived.initiative,
    passivePerception: derived.passivePerception,
    heroicInspiration: character.heroicInspiration,

    scores: character.scores,
    modifiers: Object.fromEntries(
      ABILITIES.map((a) => [a, derived.modifiers[a]]),
    ) as Record<Ability, number>,
    saveProficiencies: character.saveProficiencies,
    saveBonuses: Object.fromEntries(
      ABILITIES.map((a) => [a, derived.saves[a]]),
    ) as Record<Ability, number>,
    skillProficiencies: character.skillProficiencies,
    skillBonuses: Object.fromEntries(
      SKILL_KEYS.map((s) => [s, derived.skills[s]]),
    ) as Record<SkillKey, number>,

    attacks: character.attacks,
    classFeatures: character.features.map((f) => ({ name: f.name, text: f.description })),
    speciesTraits: character.speciesTraits,
    feats: character.feats,

    armorTraining: character.armorTraining,
    weaponTraining: character.weaponTraining || character.otherProficiencies.weapons.join(', '),
    tools: character.otherProficiencies.tools,
    languages: character.otherProficiencies.languages,
    equipment: character.equipment,
    coins: character.coins,

    alignment: character.alignment,
    appearance: character.appearance,
    backstory,

    spellcasting: derived.spellcasting
      ? {
          abilityName: ABILITY_NAMES[derived.spellcasting.ability],
          modifier: derived.spellcasting.modifier,
          saveDC: derived.spellcasting.saveDC,
          attackBonus: derived.spellcasting.attackBonus,
          slots: derived.spellcasting.slots,
          spells,
        }
      : featOnlyCasting(character, spells),
  }
}

/**
 * A non-caster can still hold spells through a Magic Initiate feat, and those
 * need a real save DC. The class grants no spellcasting, so it is derived from
 * the feat's own ability instead.
 */
function featOnlyCasting(
  character: CharacterWithStats,
  spells: SheetSpell[],
): SheetData['spellcasting'] {
  if (spells.length === 0) return null

  const feat = character.feats.find((f) => f.name.startsWith('Magic Initiate'))
  if (!feat) return { abilityName: '', modifier: 0, saveDC: 0, attackBonus: 0, slots: {}, spells }

  // Magic Initiate (Wizard) casts off Intelligence; Cleric and Druid off Wisdom.
  const ability: Ability = feat.name.includes('Wizard') ? 'int' : 'wis'
  const modifier = character.derived.modifiers[ability]
  const pb = character.derived.proficiencyBonus

  return {
    abilityName: ABILITY_NAMES[ability],
    modifier,
    saveDC: 8 + pb + modifier,
    attackBonus: pb + modifier,
    slots: {},
    spells,
  }
}

function toSheetSpell(id: string): SheetSpell | null {
  const spell = SPELLS_BY_ID.get(id)
  if (!spell) return null
  return {
    level: spell.level,
    name: spell.name,
    castingTime: spell.castingTime,
    range: spell.range,
    concentration: spell.concentration,
    ritual: spell.ritual,
    material: spell.material,
    notes: spell.duration,
  }
}

/** The Size box fits about two characters. */
const shortSize = (size: string): string =>
  size === 'Small' ? 'S' : size === 'Small or Medium' ? 'S/M' : size === 'Large' ? 'L' : 'M'
